import { useState } from 'react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import SectionHeading from '../components/SectionHeading';
import { contactDetails, seoByRoute } from '../data/siteContent';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 1) => ({ opacity: 1, y: 0, transition: { delay: 0.1 * i, duration: 0.5 } }),
};

export default function Contact() {
  const seo = seoByRoute['/contact'];
  const [formState, setFormState] = useState({ name: '', email: '', message: '', topic: 'distribution' });

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    alert('Thanks for reaching out! We will respond shortly.');
  };

  return (
    <>
      <SEO {...seo} />
      <section className="section pt-32">
        <SectionHeading
          as="h1"
          eyebrow="Connect"
          title="Let's build nourishing partnerships"
          description="Whether you're a retailer, hospitality group, school, or curious consumer, our team is ready to support your needs. Complete the form and we'll respond within 24 hours."
        />

        <div className="mt-16 grid gap-12 lg:grid-cols-[0.9fr_1.1fr]">
          <motion.div
            className="rounded-[32px] bg-gradient-to-br from-white via-slate-50 to-white p-8 shadow-xl shadow-slate-200/50 border border-slate-200"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
          >
            <h2 className="font-display text-2xl text-text">Contact details</h2>
            <ul className="mt-8 space-y-6 text-sm text-slate-600">
              <motion.li variants={fadeUp} custom={1}>
                <span className="font-semibold text-primary block mb-2">Email</span>
                <a href={`mailto:${contactDetails.email}`} className="hover:text-primary transition-colors">
                  {contactDetails.email}
                </a>
              </motion.li>
              <motion.li variants={fadeUp} custom={2}>
                <span className="font-semibold text-primary block mb-2">Phone</span>
                <div className="space-y-1">
                  <div>
                    <span className="text-xs text-slate-500">Mobile:</span>
                    <a href={`tel:${contactDetails.phone.replace(/\s/g, '')}`} className="block hover:text-primary transition-colors">
                      {contactDetails.phone}
                    </a>
                  </div>
                  <div>
                    <span className="text-xs text-slate-500">Tel:</span>
                    <a href={`tel:${contactDetails.phoneTel.replace(/\s/g, '')}`} className="block hover:text-primary transition-colors">
                      {contactDetails.phoneTel}
                    </a>
                  </div>
                </div>
              </motion.li>
              <motion.li variants={fadeUp} custom={3}>
                <span className="font-semibold text-primary block mb-2">Address</span>
                <span>{contactDetails.address}</span>
              </motion.li>
              <motion.li variants={fadeUp} custom={4}>
                <span className="font-semibold text-primary block mb-2">Office hours</span>
                <span>{contactDetails.officeHours}</span>
              </motion.li>
            </ul>
            <motion.div
              className="mt-8 rounded-[28px] bg-gradient-to-br from-secondary/10 to-transparent px-6 py-5 text-xs text-slate-600 border border-secondary/20"
              variants={fadeUp}
              custom={5}
            >
              Visitors are welcome Monday to Friday with a scheduled appointment. Group tours and tastings are available
              on the last Friday of every month.
            </motion.div>
          </motion.div>

          <motion.form
            onSubmit={handleSubmit}
            className="rounded-[32px] border border-slate-200 bg-gradient-to-br from-white to-slate-50 p-8 shadow-xl shadow-slate-200/50"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            custom={1}
          >
            <h2 className="font-display text-2xl text-text">Share your request</h2>
            <div className="mt-8 grid gap-5">
              <motion.label
                className="flex flex-col text-sm text-slate-600"
                variants={fadeUp}
                custom={2}
              >
                <span className="font-semibold text-text mb-2">Name</span>
                <input
                  type="text"
                  name="name"
                  value={formState.name}
                  onChange={handleChange}
                  required
                  className="rounded-full border border-slate-200 px-4 py-3 transition-all focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  placeholder="Your name"
                />
              </motion.label>
              <motion.label
                className="flex flex-col text-sm text-slate-600"
                variants={fadeUp}
                custom={3}
              >
                <span className="font-semibold text-text mb-2">Email</span>
                <input
                  type="email"
                  name="email"
                  value={formState.email}
                  onChange={handleChange}
                  required
                  className="rounded-full border border-slate-200 px-4 py-3 transition-all focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  placeholder="your@email.com"
                />
              </motion.label>
              <motion.label
                className="flex flex-col text-sm text-slate-600"
                variants={fadeUp}
                custom={4}
              >
                <span className="font-semibold text-text mb-2">Topic</span>
                <select
                  name="topic"
                  value={formState.topic}
                  onChange={handleChange}
                  className="rounded-full border border-slate-200 px-4 py-3 transition-all focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  <option value="distribution">Distribution partnership</option>
                  <option value="hospitality">Hospitality & catering</option>
                  <option value="retail">Retail inquiry</option>
                  <option value="media">Media & press</option>
                  <option value="other">Other</option>
                </select>
              </motion.label>
              <motion.label
                className="flex flex-col text-sm text-slate-600"
                variants={fadeUp}
                custom={5}
              >
                <span className="font-semibold text-text mb-2">Message</span>
                <textarea
                  name="message"
                  value={formState.message}
                  onChange={handleChange}
                  rows={5}
                  required
                  className="rounded-3xl border border-slate-200 px-4 py-3 transition-all focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  placeholder="Tell us more about your inquiry..."
                />
              </motion.label>
            </div>
            <motion.div
              className="mt-8 flex justify-end"
              variants={fadeUp}
              custom={6}
            >
              <button
                type="submit"
                className="btn-primary shadow-lg shadow-primary/40"
              >
                Submit Message
              </button>
            </motion.div>
          </motion.form>
        </div>
      </section>

      {/* Map Section */}
      <section className="mx-auto mt-20 max-w-6xl px-6 pb-20">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={fadeUp}
        >
          <h2 className="font-display text-3xl text-text mb-8">Visit us</h2>
          <div className="rounded-[32px] overflow-hidden shadow-xl border border-slate-200">
            <iframe
              src={contactDetails.mapEmbed}
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Duoley Location Map"
            />
          </div>
        </motion.div>
      </section>
    </>
  );
}

